<template>
    <div class="automation-listing">
        <template v-if="isLoadingTable && allAutomations.length === 0">
            <!-- TODO: add loading content -->
        </template>
        <AutomationLanding v-else-if="allAutomations.length === 0 && !isProviderDown" />
        <AutomationEmptyStateDisconnectedToProvider
            v-else-if="isProviderDown"
            class="automation-listing__empty-state"
        />
        <LayoutHeadingTabs v-else :title="translations.title">
            <template #actions>
                <AutomationCreateAutomation />
            </template>
            <template #content>
                <CompassAlert
                    v-if="automationNeedsUpdateVersion.length > 0"
                    type="warning"
                    :description="translations.alertDescription"
                    :showClose="false"
                    :action="actionAlert"
                />
                <div class="automation-listing__content">
                    <ListingSettingsHeader
                        :columns="columns"
                        :enableColumnsSettings="false"
                        searchable
                        :searchValue="searchValue"
                        @search="searchAttributes"
                    />
                    <TableLoading v-if="isLoadingTable" />
                    <CompassTable
                        v-else
                        size="large"
                        :border="true"
                        :isCheckable="false"
                        class="automation-listing__table"
                    >
                        <AutomationListingTableHead
                            :columns="columns"
                            @sort-table-head="handleSort"
                        />
                        <td
                            v-if="!automations.length"
                            colspan="100%"
                            class="automation-listing__empty-state__no-result"
                        >
                            <AutomationListingEmptyStateNoResult @reset-filters="resetFilters" />
                        </td>
                        <CompassTableBody v-else>
                            <AutomationListingTableRow
                                v-for="(automation, index) in automations"
                                :key="index"
                                :automation="automation"
                                :columns="columns"
                                @update-automation="updateAutomation"
                                @automation-duplicated="getAutomations"
                                @edit-wizard-automation="handleEditWizardAutomation"
                            />
                        </CompassTableBody>
                    </CompassTable>
                    <TablePagination
                        v-if="pagination.total > 0"
                        :pagination="pagination"
                        :isLoading="isLoadingTable"
                        @update-pagination="updatePagination"
                    />
                </div>
            </template>
        </LayoutHeadingTabs>
    </div>
</template>

<script>
import LayoutHeadingTabs from 'vcomponents/common/layout/LayoutHeadingTabs.vue';
import AutomationLanding from 'vcomponents/automation/listing/AutomationLanding.vue';
import AutomationCreateAutomation from 'vcomponents/automation/AutomationCreateAutomation.vue';
import AutomationEmptyStateDisconnectedToProvider from 'vcomponents/automation/AutomationEmptyStateDisconnectedToProvider.vue';
import AutomationListingEmptyStateNoResult from 'vcomponents/automation/listing/emptyState/AutomationListingEmptyStateNoResult.vue';
import AutomationListingTableHead from 'vcomponents/automation/listing/table/AutomationListingTableHead.vue';
import AutomationListingTableRow from 'vcomponents/automation/listing/table/AutomationListingTableRow.vue';
import ListingSettingsHeader from 'vcomponents/common/listing/header/ListingSettingsHeader.vue';
import TableLoading from 'vcomponents/common/table/TableLoading.vue';
import TablePagination from 'vcomponents/common/table/TablePagination.vue';
import AutomationMixin from 'mixins/automation/AutomationMixin.js';

import { getAutomations, getAutomationTemplates } from 'api/automation/automationService.js';
import { createNamespacedHelpers } from 'vuex';
const { mapState: mapStateSystem } = createNamespacedHelpers('system');
const { mapState: mapStatePopupPanel, mapActions: popupPanelMapActions } =
    createNamespacedHelpers('popupPanel');

export default {
    components: {
        LayoutHeadingTabs,
        AutomationLanding,
        AutomationCreateAutomation,
        AutomationEmptyStateDisconnectedToProvider,
        AutomationListingEmptyStateNoResult,
        AutomationListingTableHead,
        AutomationListingTableRow,
        ListingSettingsHeader,
        TableLoading,
        TablePagination,
    },
    mixins: [AutomationMixin],
    data() {
        return {
            translations: {
                title: this.$t.__('automation.AutomationLayout.title'),
                alertDescription: this.$t.__('automation.AutomationListing.alert.description'),
                alertLabel: this.$t.__('automation.AutomationListing.alert.action.label'),
            },
            allAutomations: [],
            automations: [],
            automationNeedsUpdateVersion: [],
            templates: [],
            columns: [
                {
                    order: 0,
                    label: this.$t.__('common.labels.name'),
                    code: 'name',
                    isDefault: true,
                    isMovable: false,
                    isVisible: true,
                    sortable: { direction: '', orderBy: 'name' },
                    maxWidth: '424px',
                },
                {
                    order: 1,
                    label: this.$t.__('common.labels.elements'),
                    code: 'elements',
                    fixed: 'right',
                    isDefault: true,
                    isMovable: false,
                    isVisible: true,
                    maxWidth: '245px',
                    minWidth: '245px',
                },
                {
                    order: 2,
                    label: this.$t.__('common.labels.last_modification'),
                    code: 'date',
                    fixed: 'right',
                    isDefault: true,
                    isMovable: false,
                    isVisible: true,
                    fitContent: true,
                    sortable: { direction: 'desc', orderBy: 'date' },
                    maxWidth: '200px',
                    minWidth: '200px',
                },
                {
                    order: 3,
                    label: this.$t.__('common.labels.status'),
                    code: 'status',
                    fixed: 'right',
                    alignContent: 'center',
                    isDefault: true,
                    isMovable: false,
                    isVisible: true,
                    fitContent: true,
                    sortable: { direction: '', orderBy: 'status' },
                    maxWidth: '64px',
                    minWidth: '64px',
                },
                {
                    order: 4,
                    label: '',
                    code: 'actions',
                    fixed: 'right',
                    isDefault: true,
                    isMovable: false,
                    isVisible: true,
                    fitContent: true,
                    maxWidth: '64px',
                    minWidth: '64px',
                },
            ],
            isLoadingTable: true,
            searchValue: '',
            pagination: {
                limit: 25,
                offset: 0,
                total: 0,
                currentPage: 1,
            },
            sort: [],
            isProviderDown: false,
        };
    },
    computed: {
        ...mapStateSystem(['errorsHandler']),
        ...mapStatePopupPanel(['automationWizardPanel']),
        isAutomationPanelOpen() {
            return this.automationWizardPanel.display;
        },
        currentOpenAutomation() {
            return this.automationWizardPanel.automation;
        },
        actionAlert() {
            return {
                label: this.translations.alertLabel,
                callback: () => this.openAutomationModalUpdateVersion(),
            };
        },
    },
    async created() {
        EventBus.on('automation-deleted', () => {
            this.getAutomations();
        });
        EventBus.on('automation-renamed', () => {
            this.getAutomations();
        });
        EventBus.on('automation-updated', () => {
            this.getAutomations();
        });
        EventBus.on('automation-updated-version', (updateSucceeded) => {
            this.updateVersionAutomation(updateSucceeded);
        });
        await this.getAutomationTemplates();
        this.initializeSort();
        this.getAutomations();
    },
    unmounted() {
        EventBus.off('automation-deleted');
        EventBus.off('automation-renamed');
        EventBus.off('automation-updated');
        EventBus.off('automation-updated-version');
    },
    methods: {
        ...popupPanelMapActions(['open', 'close']),
        handleEditWizardAutomation(automation) {
            if (this.isAutomationPanelOpen && this.currentOpenAutomation?.id === automation.id) {
                return;
            }
            if (this.isAutomationPanelOpen) {
                this.close({ key: 'automationWizardPanel' });
            }
            this.open({
                key: 'automationWizardPanel',
                props: {
                    automation: automation,
                    isEditMode: true,
                },
            });
        },
        async getAutomationTemplates() {
            getAutomationTemplates().then((response) => {
                this.templates = response.data.data.map((template) => ({
                    id: template.id,
                    version: template.metadata?.template?.version ?? '1',
                    wizard_bindings: template.metadata?.wizard_bindings ?? [],
                    wizard_schema: template.metadata?.wizard_schema ?? [],
                    wizard_steps: template.metadata?.wizard_steps ?? [],
                }));
            });
        },
        getAutomations() {
            this.isLoadingTable = true;
            this.automationNeedsUpdateVersion = [];
            getAutomations()
                .then((response) => {
                    this.allAutomations = response.data.data.map((flow) => ({
                        id: flow.id,
                        name: flow.version.displayName,
                        date: flow.updated || '-',
                        status: flow.status === 'ENABLED',
                        realStatus: flow.status,
                        actions: true,
                        version: flow.version,
                        metadata: flow.metadata,
                        pieces: [],
                    }));
                    this.updateAutomations();
                    return this.initializePiecesList(this.allAutomations);
                })
                .catch((error) => {
                    this.allAutomations = [];
                    this.isProviderDown = true;
                    this.errorsHandler.logException(error);
                    this.$pushToast({
                        type: 'error',
                        close: true,
                        text: this.$t.__('common.labels.toast.error'),
                    });
                })
                .finally(() => {
                    this.isLoadingTable = false;
                });
        },
        searchAttributes(value) {
            this.searchValue = value;
            this.updateAutomations();
        },
        updatePagination(newPagination) {
            this.pagination = {
                ...newPagination,
                offset: (newPagination.currentPage - 1) * newPagination.limit,
            };
            this.updateAutomations();
        },
        initializeSort() {
            this.sort = this.columns
                .filter((col) => col.sortable && col.sortable.orderBy)
                .map((col) => ({
                    orderBy: col.sortable.orderBy,
                    direction: col.sortable.direction,
                }));
        },
        updateAutomations() {
            if (!this.allAutomations) {
                return;
            }

            let filteredAutomations = [...this.allAutomations];

            // Backward Compatibility: Convert legacy wizard_steps to new wizard_schema + wizard_bindings structure
            // The old system used a single wizard_steps property, but we've split it into wizard_schema and wizard_bindings
            // to better handle automation template versioning (see SAAS-31332)
            filteredAutomations = filteredAutomations.map(this.backwardCompatibilityForWizardSteps);
            // Identify templates used for automation and check if newer version exists
            filteredAutomations = filteredAutomations.map(this.identifyTemplateVersionUsed);

            // Filter search value
            if (this.searchValue) {
                filteredAutomations = filteredAutomations.filter((automation) =>
                    automation.name.toLowerCase().includes(this.searchValue.toLowerCase()),
                );
            }

            // Sort columns
            filteredAutomations = this.sortAutomations(filteredAutomations);

            // Pagination
            this.pagination.total = filteredAutomations.length;
            this.pagination.offset =
                this.pagination.currentPage === 1
                    ? 0
                    : (this.pagination.currentPage - 1) * this.pagination.limit;
            const start = this.pagination.offset;
            const end = start + this.pagination.limit;
            this.automations = filteredAutomations.slice(start, end);
        },
        backwardCompatibilityForWizardSteps(automation) {
            // See SAAS-31332 for details
            if (!automation?.metadata || automation.metadata.wizard_schema) {
                return automation;
            }
            automation.metadata.wizard_bindings = {};
            automation.metadata.wizard_schema = _.cloneDeep(automation.metadata.wizard_steps);
            automation.metadata.wizard_schema.map(function (step) {
                if (!step?.fields) return step;
                step.fields.map(function (field) {
                    if (field?.id && field?.target) {
                        automation.metadata.wizard_bindings[field.id] = field.target;
                        delete field.target;
                    }
                    return field;
                });
                return step;
            });
            return automation;
        },
        identifyTemplateVersionUsed(automation) {
            // Identify templates used for automation and check if newer version exists
            const templateId = automation?.metadata?.template?.id;

            if (!templateId) {
                return automation;
            }

            const template = this.templates.find((template) => template.id === templateId);

            if (!template) {
                return automation;
            }

            const availableTemplateVersion = parseFloat(template.version);
            const appliedTemplateVersion = parseFloat(
                automation?.metadata?.template?.version ?? '1',
            );

            const versionDiff = availableTemplateVersion - appliedTemplateVersion;

            if (versionDiff === 0) {
                return automation;
            }

            automation.hasTemplateVersionUpdate = true;

            if (!automation.hasTemplateVersionUpdate) {
                return automation;
            }

            const majorVersionDiff =
                Math.floor(availableTemplateVersion) - Math.floor(appliedTemplateVersion);

            const minorVersionDiff = (
                (availableTemplateVersion % 1) -
                (appliedTemplateVersion % 1)
            ).toFixed(2);

            automation.versionInfo = {
                applied: appliedTemplateVersion.toFixed(1),
                available: template.version,
                diff: versionDiff.toFixed(1),
            };

            if (majorVersionDiff > 0) {
                automation.versionInfo.updateType = 'major';
            } else if (minorVersionDiff >= 0.1) {
                automation.versionInfo.updateType = 'minor';
                this.automationNeedsUpdateVersion.push(automation);
            }

            return automation;
        },
        sortAutomations(automations) {
            for (const sort of this.sort) {
                if (sort.direction) {
                    automations.sort((a, b) => {
                        let comparison = 0;
                        const orderBy = sort.orderBy;

                        if (orderBy === 'date') {
                            const aValue = new Date(a.date).getTime() || 0;
                            const bValue = new Date(b.date).getTime() || 0;
                            comparison = aValue - bValue;
                        } else if (orderBy === 'name') {
                            comparison = a.name.localeCompare(b.name, undefined, {
                                numeric: true,
                                sensitivity: 'base',
                            });
                        } else if (orderBy === 'status') {
                            const normalizeStatus = (status) => {
                                if (status === true) return 1;
                                if (status === false) return 0;
                                return -1;
                            };
                            const aValue = normalizeStatus(a.status);
                            const bValue = normalizeStatus(b.status);
                            comparison = aValue - bValue;
                        } else {
                            comparison = a[orderBy] - b[orderBy];
                        }

                        return sort.direction === 'asc' ? comparison : -comparison;
                    });
                }
            }
            return automations;
        },
        updateAutomation({ id, status }) {
            const automation = this.allAutomations.find((automation) => automation.id === id);
            if (automation) {
                automation.status = status;
            }
            this.updateAutomations();
        },
        handleSort(sortParams) {
            const orderTransitions = {
                asc: 'desc',
                desc: '',
                '': 'asc',
            };
            const existingSort = this.sort.find((s) => s.orderBy === sortParams.orderBy);
            if (existingSort) {
                existingSort.direction = orderTransitions[existingSort.direction];
                if (!existingSort.direction) {
                    this.sort = this.sort.filter((s) => s.orderBy !== sortParams.orderBy);
                }
            } else {
                this.sort.push({ orderBy: sortParams.orderBy, direction: 'asc' });
            }
            this.updateAutomations();
        },
        resetFilters() {
            this.searchValue = '';
            this.initializeSort();
            this.updateAutomations();
        },
        openAutomationModalUpdateVersion() {
            this.open({
                key: 'automationModalUpdateVersion',
                props: {
                    automations: this.automationNeedsUpdateVersion,
                    templates: this.templates,
                },
            });
        },
        updateVersionAutomation(result) {
            this.getAutomations();

            if (result.success) {
                this.$pushToast({
                    type: 'success',
                    close: true,
                    icon: true,
                    text: this.$t.__('automation.AutomationModalUpdateVersion.toast.success'),
                });
            } else if (result.failureCount === result.total) {
                // All updates have failed
                this.$pushToast({
                    type: 'error',
                    close: true,
                    icon: true,
                    text: this.$t.__('automation.AutomationModalUpdateVersion.toast.error.all'),
                });
            } else {
                // Some updates have failed
                this.$pushToast({
                    type: 'warning',
                    close: true,
                    icon: true,
                    text: this.$t.__(
                        'automation.AutomationModalUpdateVersion.toast.error.partial',
                        {
                            success: result.successCount,
                            failure: result.failureCount,
                        },
                    ),
                });
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.automation-listing {
    height: 100%;

    &__content {
        display: flex;
        flex-direction: column;
        background: var(--colors-common-background-default);
        padding: 0 var(--spacing-3);
        border-radius: var(--spacing-0);
        overflow-y: auto;
    }

    &__table {
        flex: 1;
        overflow: auto;
    }

    &__empty-state {
        height: 560px;
        display: flex;
        justify-content: center;
        background-color: var(--colors-common-background-default);

        &__no-result {
            padding: 112px;
        }
    }
}
</style>

<style lang="scss">
.automation-listing .layout-heading-tabs__content {
    max-height: calc(100% - 56px);
    height: 100%;
}

.compass-menu__dropdown__container.automation-listing__actions-menu {
    z-index: 3;
}
</style>
